import type {
  Direction, Pos, Board, PlacedEmitter, SignalMap, ClueStatus, EmitterShape,
} from "../types.ts";
import { ALL_DIRECTIONS, posKey } from "../types.ts";
import {
  boardRows, boardCols, inBounds, getCell, getNeighborPos,
  createSignalMap, incrementSignal, getSignal,
} from "./grid.ts";

// ── Arm computation ──

/** Base arms for each emitter shape at rotation 0 */
function baseArms(shape: EmitterShape): Direction[] {
  switch (shape) {
    case "single": return ["up"];
    case "L": return ["up", "right"];
    case "line": return ["up", "down"];
    case "cross": return ["up", "right", "down", "left"];
  }
}

/** Rotate a direction by N quarter-turns clockwise */
function rotateDirection(dir: Direction, turns: number): Direction {
  const idx = ALL_DIRECTIONS.indexOf(dir);
  return ALL_DIRECTIONS[(idx + turns) % 4]!;
}

/** Get the active arm directions for an emitter at its rotation */
export function getArms(shape: EmitterShape, rotation: number): Direction[] {
  return baseArms(shape).map((d) => rotateDirection(d, rotation));
}

/** Number of distinct rotations for a shape */
export function distinctRotations(shape: EmitterShape): number {
  switch (shape) {
    case "cross": return 1;
    case "line": return 2;
    case "single": return 4;
    case "L": return 4;
  }
}

// ── Beam tracing ──

/**
 * Trace a beam from origin in direction for up to `range` cells.
 * Stops at: grid boundary, wall cell, another emitter.
 * Returns the list of cells the beam passes through (NOT including origin).
 */
export function traceBeam(
  board: Board,
  origin: Pos,
  direction: Direction,
  range: number,
  emitterPositions: Set<string>
): Pos[] {
  const cells: Pos[] = [];
  let current = origin;

  for (let i = 0; i < range; i++) {
    const next = getNeighborPos(current, direction);
    if (!inBounds(board, next)) break;

    const cell = getCell(board, next);
    if (!cell || cell.kind === "wall") break;

    // Stop if another emitter is in the way
    if (emitterPositions.has(posKey(next))) break;

    cells.push(next);
    current = next;
  }

  return cells;
}

// ── Signal map computation ──

/** Build a set of all emitter position keys for fast lookup */
function emitterPosSet(emitters: PlacedEmitter[]): Set<string> {
  const s = new Set<string>();
  for (const e of emitters) s.add(posKey(e.pos));
  return s;
}

/**
 * Compute signal counts for every cell on the board.
 * Each arm of each emitter traces a beam; every cell the beam passes
 * through gets +1 signal.
 */
export function computeSignalMap(board: Board, emitters: PlacedEmitter[]): SignalMap {
  const rows = boardRows(board);
  const cols = boardCols(board);
  const map = createSignalMap(rows, cols);
  const posSet = emitterPosSet(emitters);

  for (const emitter of emitters) {
    const arms = getArms(emitter.type.shape, emitter.rotation);
    for (const dir of arms) {
      const cells = traceBeam(board, emitter.pos, dir, emitter.type.range, posSet);
      for (const c of cells) {
        incrementSignal(map, c);
      }
    }
  }

  return map;
}

// ── Clue evaluation ──

/** Evaluate each clue cell: is its signal count satisfied, exceeded, or unsatisfied? */
export function evaluateClues(
  board: Board,
  signalMap: SignalMap
): Map<string, ClueStatus> {
  const result = new Map<string, ClueStatus>();
  const rows = boardRows(board);
  const cols = boardCols(board);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = getCell(board, { row: r, col: c });
      if (!cell || cell.kind !== "clue") continue;

      const key = posKey({ row: r, col: c });
      const actual = getSignal(signalMap, { row: r, col: c });
      const target = cell.clueValue ?? 0;

      if (actual === target) {
        result.set(key, "satisfied");
      } else if (actual > target) {
        result.set(key, "exceeded");
      } else {
        result.set(key, "unsatisfied");
      }
    }
  }

  return result;
}

/** Check if the puzzle is solved: all emitters placed + all clues satisfied */
export function checkWin(
  board: Board,
  emitters: PlacedEmitter[],
  inventoryEmpty: boolean
): boolean {
  if (!inventoryEmpty) return false;

  const signalMap = computeSignalMap(board, emitters);
  const clues = evaluateClues(board, signalMap);

  for (const status of clues.values()) {
    if (status !== "satisfied") return false;
  }

  return true;
}
